import React, { useState, useRef, useEffect } from 'react';
import {
  STATUS_CONFIG, TYPE_CONFIG,
  getProjectRawShared, getProjectTotalShared, getMonthlyTeamCost,
  monthsBetween, formatCurrency, formatAED, formatDate, getDevById, getProjectProgress,
} from '../utils';

// ── Small reusable components ─────────────────────────────────────────────────

function EditableDate({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <input
        type="date"
        defaultValue={value}
        autoFocus
        onBlur={e => { if (e.target.value) onChange(e.target.value); setEditing(false); }}
        onKeyDown={e => { if (e.key === 'Escape') setEditing(false); }}
        style={{
          background: '#1e293b', border: '1px solid #3b82f6', borderRadius: 4,
          color: '#e2e8f0', fontSize: 11, padding: '2px 4px', outline: 'none',
          fontFamily: 'inherit', width: 120,
        }}
      />
    );
  }
  return (
    <span
      onClick={e => { e.stopPropagation(); setEditing(true); }}
      title="Click to edit date"
      style={{ cursor: 'text', borderBottom: '1px dotted #475569', fontSize: 11, color: '#64748b' }}
    >
      {new Date(value).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
    </span>
  );
}

function ProgressBar({ pct, color }) {
  return (
    <div style={{ height: 4, background: '#1e293b', borderRadius: 2, overflow: 'hidden', width: 80 }}>
      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 2, transition: 'width 0.3s' }} />
    </div>
  );
}

// Clickable status badge → dropdown to change status
function StatusSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState({ x: 0, y: 0 });
  const btnRef = useRef(null);
  const cfg = STATUS_CONFIG[value] || STATUS_CONFIG['planned'];

  function handleClick(e) {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ x: r.left, y: r.bottom + 4 });
    }
    setOpen(o => !o);
  }

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  return (
    <>
      <span
        ref={btnRef}
        onClick={handleClick}
        title="Click to change status"
        style={{
          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
          background: cfg.color + '22', color: cfg.color,
          border: `1px solid ${cfg.color}44`,
          cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}
      >
        {cfg.label}<span style={{ fontSize: 8, opacity: 0.6 }}>▾</span>
      </span>
      {open && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'fixed', left: pos.x, top: pos.y,
            zIndex: 9999, background: '#0f172a',
            border: '1px solid #1e293b', borderRadius: 6,
            padding: 4, minWidth: 140,
            boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
          }}
        >
          {Object.entries(STATUS_CONFIG).map(([key, c]) => (
            <div
              key={key}
              onClick={() => { onChange(key); setOpen(false); }}
              style={{
                padding: '7px 10px', borderRadius: 4, cursor: 'pointer',
                fontSize: 11, fontWeight: 700, color: c.color,
                background: key === value ? c.color + '22' : 'transparent',
              }}
            >{c.label}</div>
          ))}
        </div>
      )}
    </>
  );
}

// ── Add Project Modal ─────────────────────────────────────────────────────────

function AddProjectModal({ projects, developers, onAdd, onClose }) {
  const mainProjects = projects.filter(p => p.type === 'main');
  const [form, setForm] = useState({
    name: '', description: '', type: 'standalone', parentId: '',
    status: 'planned', phase: '', startDate: '', endDate: '',
    assignedDevIds: [], tags: '', notes: '',
  });
  const [error, setError] = useState('');

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  function toggleDev(id) {
    setForm(f => ({
      ...f,
      assignedDevIds: f.assignedDevIds.includes(id)
        ? f.assignedDevIds.filter(d => d !== id)
        : [...f.assignedDevIds, id],
    }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim())  return setError('Project name is required');
    if (!form.startDate)    return setError('Start date is required');
    if (!form.endDate)      return setError('End date is required');
    if (form.endDate < form.startDate) return setError('End date must be after start date');

    onAdd({
      name:         form.name.trim(),
      description:  form.description.trim(),
      type:         form.type,
      parentId:     form.type === 'sub' ? (form.parentId || null) : null,
      status:       form.status,
      phase:        form.phase.trim() || null,
      startDate:    form.startDate,
      endDate:      form.endDate,
      assignedDevIds: form.assignedDevIds,
      tags:         form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      notes:        form.notes.trim(),
    });
    onClose();
  }

  const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: 6,
    background: '#0a0f1e', border: '1px solid #1e293b',
    color: '#e2e8f0', fontSize: 12, outline: 'none',
    fontFamily: 'inherit', boxSizing: 'border-box',
  };
  const labelStyle = {
    fontSize: 10, fontWeight: 700, color: '#475569',
    letterSpacing: 0.8, marginBottom: 4, display: 'block',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#0f172a', border: '1px solid #1e293b',
          borderRadius: 14, padding: '28px 32px',
          width: 560, maxHeight: '85vh', overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#f1f5f9' }}>Add New Project</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#475569', fontSize: 20, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>PROJECT NAME *</label>
            <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. New Client – Website" autoFocus />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>TYPE</label>
              <select style={inputStyle} value={form.type} onChange={e => set('type', e.target.value)}>
                <option value="standalone">Standalone</option>
                <option value="sub">Sub-module (under main)</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>STATUS</label>
              <select style={inputStyle} value={form.status} onChange={e => set('status', e.target.value)}>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>

          {form.type === 'sub' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>PARENT PROJECT</label>
                <select style={inputStyle} value={form.parentId} onChange={e => set('parentId', e.target.value)}>
                  <option value="">— Select —</option>
                  {mainProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>PHASE</label>
                <input style={inputStyle} value={form.phase} onChange={e => set('phase', e.target.value)} placeholder="e.g. ERP Ph.4" />
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>START DATE *</label>
              <input type="date" style={inputStyle} value={form.startDate} onChange={e => set('startDate', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>END DATE *</label>
              <input type="date" style={inputStyle} value={form.endDate} onChange={e => set('endDate', e.target.value)} />
            </div>
          </div>

          <div>
            <label style={labelStyle}>DESCRIPTION</label>
            <input style={inputStyle} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Brief description" />
          </div>

          <div>
            <label style={labelStyle}>TAGS (comma separated)</label>
            <input style={inputStyle} value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="website, client, erp" />
          </div>

          <div>
            <label style={labelStyle}>ASSIGN DEVELOPERS</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {developers.map(d => {
                const on = form.assignedDevIds.includes(d.id);
                return (
                  <div key={d.id} onClick={() => toggleDev(d.id)} style={{
                    padding: '5px 10px', borderRadius: 6, cursor: 'pointer',
                    fontSize: 11, fontWeight: 600, userSelect: 'none',
                    background: on ? '#1e3a6e' : '#0a0f1e',
                    color: on ? '#60a5fa' : '#64748b',
                    border: `1px solid ${on ? '#3b82f6' : '#1e293b'}`,
                    transition: 'all 0.1s',
                  }}>
                    {d.name}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <label style={labelStyle}>NOTES</label>
            <input style={inputStyle} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional" />
          </div>

          {error && <div style={{ color: '#ef4444', fontSize: 11 }}>{error}</div>}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{
              padding: '9px 20px', borderRadius: 7, border: '1px solid #1e293b',
              background: 'transparent', color: '#64748b', fontSize: 12,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>Cancel</button>
            <button type="submit" style={{
              padding: '9px 20px', borderRadius: 7, border: 'none',
              background: '#1e3a6e', color: '#60a5fa', fontSize: 12,
              fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 0.5,
            }}>Add Project</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Project row ───────────────────────────────────────────────────────────────

function ProjectRow({ project, isExpanded, onToggle, allProjects, developers, onUpdateProject, isCollapsed, onToggleCollapse, subCount }) {
  const statusCfg = STATUS_CONFIG[project.status] || STATUS_CONFIG['planned'];
  const typeCfg   = TYPE_CONFIG[project.type];
  const raw     = getProjectRawShared(project, allProjects, developers);
  const total   = getProjectTotalShared(project, allProjects, developers);
  const monthly = getMonthlyTeamCost(project, developers);
  const months  = monthsBetween(project.startDate, project.endDate);
  const pct     = getProjectProgress(project);
  const isMain  = project.type === 'main';
  const isSub   = project.type === 'sub';

  const subBurnRaw   = isMain ? allProjects.filter(p => p.parentId === project.id).reduce((s, p) => s + getProjectRawShared(p, allProjects, developers), 0) : null;
  const subBurnTotal = isMain ? allProjects.filter(p => p.parentId === project.id).reduce((s, p) => s + getProjectTotalShared(p, allProjects, developers), 0) : null;

  return (
    <>
      <tr
        onClick={onToggle}
        style={{
          cursor: 'pointer',
          background: isExpanded ? '#0d1f3c' : (isMain ? '#0b1d38' : isSub ? '#080c18' : '#0a0f1e'),
          borderLeft: `4px solid ${isMain ? '#3b82f6' : isSub ? '#1e3a5f' : '#1e293b'}`,
          transition: 'background 0.1s',
        }}
      >
        {/* Project name */}
        <td style={{ padding: '11px 12px', paddingLeft: isSub ? 36 : 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isMain && (
              <button
                onClick={e => { e.stopPropagation(); onToggleCollapse && onToggleCollapse(); }}
                title={isCollapsed ? 'Expand sub-projects' : 'Collapse sub-projects'}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#3b82f6', fontSize: 11, padding: '2px 3px', lineHeight: 1,
                  flexShrink: 0, transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s', display: 'inline-flex', alignItems: 'center',
                }}
              >▼</button>
            )}
            {isSub && <span style={{ color: '#1e40af', fontSize: 12, flexShrink: 0, opacity: 0.6, marginLeft: -8 }}>└</span>}
            <span style={{ fontSize: isSub ? 12 : 14 }}>{typeCfg.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: isMain ? 800 : 600, fontSize: isMain ? 13 : 12, color: isMain ? '#e2e8f0' : isSub ? '#93c5fd' : '#cbd5e1' }}>
                  {project.name}
                </span>
                {isMain && subCount > 0 && (
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: '#0f2a4a', color: '#60a5fa', border: '1px solid #1e3a5f' }}>
                    {isCollapsed ? `${subCount} hidden` : `${subCount} modules`}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 10, color: '#475569', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 300 }}>
                {project.description}
              </div>
            </div>
          </div>
        </td>

        {/* Status — clickable dropdown */}
        <td style={{ padding: '11px 8px' }} onClick={e => e.stopPropagation()}>
          <StatusSelect value={project.status} onChange={s => onUpdateProject(project.id, { status: s })} />
        </td>

        <td style={{ padding: '11px 8px' }}>
          {project.phase
            ? <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#1e293b', color: '#94a3b8', border: '1px solid #33415544', whiteSpace: 'nowrap' }}>{project.phase}</span>
            : <span style={{ color: '#334155', fontSize: 11 }}>—</span>
          }
        </td>

        <td style={{ padding: '11px 8px', whiteSpace: 'nowrap' }}>
          <EditableDate value={project.startDate} onChange={d => onUpdateProject(project.id, { startDate: d })} />
        </td>
        <td style={{ padding: '11px 8px', whiteSpace: 'nowrap' }}>
          <EditableDate value={project.endDate} onChange={d => onUpdateProject(project.id, { endDate: d })} />
        </td>
        <td style={{ padding: '11px 8px', textAlign: 'center', fontSize: 12, color: '#94a3b8' }}>{months} mo</td>
        <td style={{ padding: '11px 8px', textAlign: 'center', fontSize: 12, color: '#94a3b8' }}>{project.assignedDevIds.length}</td>
        <td style={{ padding: '11px 8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ProgressBar pct={pct} color={statusCfg.color} />
            <span style={{ fontSize: 10, color: '#475569', width: 28 }}>{pct}%</span>
          </div>
        </td>
        <td style={{ padding: '11px 8px', textAlign: 'right' }}>
          <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700 }}>{formatCurrency(isMain ? subBurnRaw : raw)}</div>
          <div style={{ fontSize: 10, color: '#475569' }}>{formatAED(isMain ? subBurnRaw : raw)}</div>
        </td>
        <td style={{ padding: '11px 8px', textAlign: 'right' }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#fbbf24' }}>{formatCurrency(isMain ? subBurnTotal : total)}</div>
          <div style={{ fontSize: 10, color: '#64748b' }}>{formatAED(isMain ? subBurnTotal : total)}</div>
        </td>
        <td style={{ padding: '11px 8px', textAlign: 'center', color: '#334155', fontSize: 11 }}>{isExpanded ? '▲' : '▼'}</td>
      </tr>

      {/* Expanded detail panel */}
      {isExpanded && (
        <tr style={{ background: '#081020' }}>
          <td colSpan={11} style={{ padding: '16px 20px 16px 40px' }}>
            <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>

              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 10, color: '#334155', fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>DETAILS</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ color: '#475569', fontSize: 11, width: 80, flexShrink: 0 }}>Tags:</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {project.tags.map(t => (
                        <span key={t} style={{ fontSize: 10, padding: '1px 7px', borderRadius: 4, background: '#1e293b', color: '#64748b' }}>{t}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ color: '#475569', fontSize: 11, width: 80, flexShrink: 0 }}>Notes:</span>
                    <span style={{ color: '#94a3b8', fontSize: 11 }}>{project.notes || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ color: '#475569', fontSize: 11, width: 80, flexShrink: 0 }}>Monthly:</span>
                    <span style={{ color: '#94a3b8', fontSize: 11 }}>{formatCurrency(monthly)}/month</span>
                  </div>
                </div>
              </div>

              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 10, color: '#334155', fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>ASSIGNED DEVELOPERS</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {project.assignedDevIds.map(id => {
                    const dev = getDevById(id, developers);
                    return dev ? (
                      <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                        <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{dev.name}</span>
                        <span style={{ color: '#475569' }}>{dev.role}</span>
                        <span style={{ color: '#fbbf24', marginLeft: 'auto' }}>{formatCurrency(dev.monthlySalary)}/mo</span>
                        <button
                          onClick={e => { e.stopPropagation(); onUpdateProject(project.id, { assignedDevIds: project.assignedDevIds.filter(i => i !== id) }); }}
                          title="Remove from project"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 15, padding: '0 2px', lineHeight: 1, opacity: 0.7, flexShrink: 0 }}
                        >×</button>
                      </div>
                    ) : null;
                  })}
                  {developers.filter(d => !project.assignedDevIds.includes(d.id)).length > 0 && (
                    <select
                      value=""
                      onChange={e => { if (!e.target.value) return; onUpdateProject(project.id, { assignedDevIds: [...project.assignedDevIds, e.target.value] }); }}
                      onClick={e => e.stopPropagation()}
                      style={{ marginTop: 6, padding: '4px 8px', borderRadius: 4, background: '#0a0f1e', border: '1px dashed #334155', color: '#64748b', fontSize: 11, cursor: 'pointer', outline: 'none', width: '100%' }}
                    >
                      <option value="">+ Add developer...</option>
                      {developers.filter(d => !project.assignedDevIds.includes(d.id)).map(d => (
                        <option key={d.id} value={d.id}>{d.name} — {d.role}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              <div style={{ minWidth: 200 }}>
                <div style={{ fontSize: 10, color: '#334155', fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>BURN CALCULATION</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    ['Raw burn (shared)', raw, '#94a3b8'],
                    ['+ 20% overhead',    Math.round(raw * 0.2), '#f97316'],
                    ['Total Burn',        total, '#fbbf24'],
                  ].map(([label, val, color]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, paddingTop: 6 }}>
                      <span style={{ color, fontSize: 12, fontWeight: label === 'Total Burn' ? 700 : 400 }}>{label}</span>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color, fontWeight: 700, fontSize: label === 'Total Burn' ? 14 : 12 }}>{formatCurrency(val)}</div>
                        <div style={{ color: '#475569', fontSize: 10 }}>{formatAED(val)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Main table component ──────────────────────────────────────────────────────

export default function ProjectsTable({ projects, developers, onUpdateProject, onAddProject }) {
  const [expandedId,    setExpandedId]    = useState(null);
  const [search,        setSearch]        = useState('');
  const [filterStatus,  setFilterStatus]  = useState('all');
  const [filterType,    setFilterType]    = useState('all');
  const [collapsedMains, setCollapsedMains] = useState(new Set());
  const [showAddModal,  setShowAddModal]  = useState(false);

  function toggleMainCollapse(id) {
    setCollapsedMains(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const mainProjects = projects.filter(p => p.type === 'main');
  const standalone   = projects.filter(p => p.type === 'standalone');
  const ordered = [];
  for (const mp of mainProjects) {
    ordered.push(mp);
    ordered.push(...projects.filter(p => p.parentId === mp.id));
  }
  ordered.push(...standalone);

  const filtered = ordered.filter(p => {
    if (p.type === 'sub' && p.parentId && collapsedMains.has(p.parentId)) return false;
    const matchSearch = !search ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.description.toLowerCase().includes(search.toLowerCase()) ||
      p.tags.some(t => t.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = filterStatus === 'all' || p.status === filterStatus;
    const matchType   = filterType   === 'all' || p.type   === filterType;
    return matchSearch && matchStatus && matchType;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', gap: 12, padding: '12px 16px',
        borderBottom: '1px solid #1e293b', background: '#0a0f1e',
        flexWrap: 'wrap', alignItems: 'center', flexShrink: 0,
      }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍  Search projects, tags, descriptions..."
          style={{
            flex: 1, minWidth: 200, maxWidth: 380, padding: '7px 12px', borderRadius: 7,
            background: '#0f172a', border: '1px solid #1e293b',
            color: '#e2e8f0', fontSize: 12, outline: 'none',
          }}
        />
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 7, background: '#0f172a', border: '1px solid #1e293b', color: '#e2e8f0', fontSize: 12, outline: 'none' }}
        >
          <option value="all">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          style={{ padding: '7px 12px', borderRadius: 7, background: '#0f172a', border: '1px solid #1e293b', color: '#e2e8f0', fontSize: 12, outline: 'none' }}
        >
          <option value="all">All Types</option>
          <option value="main">Main Project</option>
          <option value="sub">Sub-Modules</option>
          <option value="standalone">Standalone</option>
        </select>
        <span style={{ fontSize: 11, color: '#334155' }}>{filtered.length} projects</span>
        <button
          onClick={() => setShowAddModal(true)}
          style={{
            marginLeft: 'auto', padding: '7px 16px', borderRadius: 7, border: 'none',
            background: '#1e3a6e', color: '#60a5fa', fontWeight: 700, fontSize: 12,
            cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 0.5,
            whiteSpace: 'nowrap',
          }}
        >+ Add Project</button>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
            <tr style={{ background: '#0d1117' }}>
              {['Project & Description', 'Status', 'Phase', 'Start', 'End', 'Duration', 'Team', 'Progress', 'Raw Burn', 'With OH', ''].map(h => (
                <th key={h} style={{
                  padding: '10px 8px', textAlign: h === 'Project & Description' ? 'left' : 'center',
                  fontSize: 10, fontWeight: 700, color: '#334155',
                  letterSpacing: 0.8, borderBottom: '2px solid #1e293b',
                  whiteSpace: 'nowrap', userSelect: 'none',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <ProjectRow
                key={p.id}
                project={p}
                isExpanded={expandedId === p.id}
                onToggle={() => setExpandedId(id => id === p.id ? null : p.id)}
                allProjects={projects}
                developers={developers}
                onUpdateProject={onUpdateProject}
                isCollapsed={p.type === 'main' ? collapsedMains.has(p.id) : false}
                onToggleCollapse={p.type === 'main' ? () => toggleMainCollapse(p.id) : null}
                subCount={p.type === 'main' ? projects.filter(s => s.parentId === p.id).length : 0}
              />
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: '#334155', fontSize: 14 }}>
            No projects match your filters.
          </div>
        )}
      </div>

      {showAddModal && (
        <AddProjectModal
          projects={projects}
          developers={developers}
          onAdd={onAddProject}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
}
